/* A task row → its MEANING + slot values (SENTENCES, NOT LABELS, wave 1).
 *
 * Every value traces to a record field on the task: fund_name, created_at,
 * task_data (recon diagnosis figures, chase object ids) and the engine
 * title, which is the ONLY place the object's name lives today for most
 * task types. The object is read off the title by the producers' own
 * separators ("Chain review — X", "Look-through review: X"); when no
 * separator is present the title itself is the object — a record field
 * verbatim, never an invented name. (Wave 2 asks the queue endpoint for
 * an `object_label` so this parsing can retire.) */

import type { ReconTaskData, TaskQueueItem } from '../lib/tasks'
import { ageWords, type SlotValue } from '../lib/sentences/compile'
import { chaseObjectLabelOf, chaseObligationLabelOf, isEvidenceChase } from './chaseGroups'

export interface TaskSentenceSpec {
  meaning: string
  values: Record<string, SlotValue>
  /** The engine title — the fallback the generic meaning prints verbatim. */
  fallback: string
}

const SEPARATORS = [' — ', ': ']

/** The object half of an engine title. */
export function taskObjectLabel(task: TaskQueueItem): string | null {
  const chase = isEvidenceChase(task) ? chaseObjectLabelOf(task) : null
  if (chase) return chase
  const title = (task.title || '').replace(/\s*\(recommended\)\s*$/, '').trim()
  if (!title) return null
  // The producers' own separators — whichever comes FIRST splits kind
  // from object ("Position recon (trade missing): Hartmann — Namenaktien
  // — Silvretta" keeps the whole holder — instrument — issuer path).
  const cuts = SEPARATORS.map((sep) => ({ sep, at: title.indexOf(sep) })).filter((c) => c.at > 0)
  if (cuts.length) {
    const first = cuts.sort((a, b) => a.at - b.at)[0]
    return title.slice(first.at + first.sep.length).trim() || null
  }
  const m = /^Missing trade for (.+?)(?: position| instrument)?$/.exec(title)
  if (m) return m[1]
  return title
}

/** evidence_approval titles read "Evidence sign-off — <obligation (code)>
 *  — <object …>" — the same shape as a chase title. */
function signoffParts(task: TaskQueueItem): { obligation: string | null; object: string | null } {
  const parts = (task.title || '').split(' — ')
  if (parts.length >= 3 && parts[0].startsWith('Evidence sign-off'))
    return { obligation: parts[1], object: parts.slice(2).join(' — ') }
  return { obligation: null, object: taskObjectLabel(task) }
}

/** proposal_review titles read "Review — from N17, N23": the documents
 *  the chain was extracted from are the object. */
function proposalDocuments(object: string | null): string | null {
  if (!object) return null
  return object.replace(/^from\s+/i, '') || null
}

const KNOWN_MEANINGS = new Set([
  'source_question',
  'proposal_review',
  'evidence_approval',
  'verification_signoff',
  'verification_remediation',
  'investigation_action',
  'request_overdue',
  'request_unlocked_copy',
  'document_stuck',
  'dead_letter_review',
  'look_through_review',
  'look_through_incomplete',
  'chain_orphan',
  'position_trade_missing',
  'instrument_trade_missing',
  'position_balance_stale',
  'instrument_balance_stale',
  'position_balance_rollforward',
  'position_leg_identity_violation',
  'commitment_supply_mismatch',
])

export function taskSentence(task: TaskQueueItem, now: number = Date.now()): TaskSentenceSpec {
  const fund = task.fund_name || null
  const age = ageWords(task.created_at, now)
  const object = taskObjectLabel(task)
  const td = (task.task_data ?? {}) as ReconTaskData & Record<string, unknown>
  const diag = td.diagnosis
  const base: Record<string, SlotValue> = { fund, age, object }
  const fallback = task.title || ''

  if (!KNOWN_MEANINGS.has(task.task_type)) {
    return { meaning: 'task.generic', values: { title: task.title, fund, age }, fallback }
  }
  const meaning = `task.${task.task_type}`
  switch (task.task_type) {
    case 'proposal_review':
      return { meaning, values: { documents: proposalDocuments(object), fund, age }, fallback }
    case 'evidence_approval': {
      const p = signoffParts(task)
      // When the demand type is not resolved, drop the "of <demand>" clause
      // entirely rather than print engine-speak (Ale 2026-09-03) — the named
      // variant only runs when we actually have the demand's name.
      if (!p.obligation)
        return {
          meaning: 'task.evidence_approval.unnamed',
          values: { object: p.object, fund, age },
          fallback,
        }
      return {
        meaning,
        values: { obligation: p.obligation, object: p.object, fund, age },
        fallback,
      }
    }
    case 'investigation_action':
      return {
        meaning,
        values: { ...base, obligation: chaseObligationLabelOf(task) },
        fallback,
      }
    case 'request_overdue':
      return { meaning, values: { counterparty: object, fund, age }, fallback }
    case 'source_question':
    case 'request_unlocked_copy':
    case 'document_stuck':
      return { meaning, values: { document: object, fund, age }, fallback }
    case 'look_through_review':
      return { meaning, values: { entity: object, fund, age }, fallback }
    case 'position_trade_missing':
    case 'instrument_trade_missing':
      return {
        meaning,
        values: {
          ...base,
          delta: diag?.delta ?? diag?.suggested_units_delta ?? td.expected_units_delta ?? null,
          balance_date: diag?.balance_as_of ?? td.balance_update_date ?? null,
        },
        fallback,
      }
    case 'position_balance_stale':
    case 'instrument_balance_stale':
      // No stored figure → drop the record clause rather than say a
      // placeholder for it (Ale 2026-09-04, copy path).
      if (diag?.stored == null)
        return {
          meaning: `${meaning}.nostored`,
          values: { ...base, computed: diag?.computed ?? null },
          fallback,
        }
      return {
        meaning,
        values: { ...base, stored: diag.stored, computed: diag?.computed ?? null },
        fallback,
      }
    case 'position_balance_rollforward':
      return { meaning, values: { ...base, computed: diag?.computed ?? null }, fallback }
    default:
      return { meaning, values: base, fallback }
  }
}
